import { useMemo } from 'react';
import DashboardCard, { EmptyState } from './DashboardCard';
import CardGrid from './CardGrid';
import { cardDateColumn, yesOrBlank, type CardColumn } from './cardColumns';
import { useGlobalStore } from '../../stores/globalStore';
import type { DashboardDischargedResident } from '../../types/dashboard';
import { useDashboardNav, RESIDENT_TAB } from './useDashboardNav';

interface Props {
  data: DashboardDischargedResident[];
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
}

export default function UnitRoomBedChangesCard({ data, ...dragProps }: Props) {
  const { rowLinkProps } = useDashboardNav();
  const roomLabel = useGlobalStore((s) => s.roomLabel);

  const columns = useMemo<CardColumn<DashboardDischargedResident>[]>(() => [
    cardDateColumn<DashboardDischargedResident>('dateTimeOfLog', 'Date', { sortOrder: 'desc' }),
    { dataField: 'fullName', caption: 'Name', sizing: 'fill' },
    { dataField: 'unit', caption: 'Unit' },
    // A number, sorted as one, shown as typed (no thousands separator).
    { dataField: 'room_No', caption: roomLabel, format: (v) => String(v) },
    { dataField: 'bed_No', caption: 'Bed' },
    {
      name: 'admit',
      caption: 'Admit',
      calculateCellValue: (row) => row.fKey_Log_Interaction_Type === 1,
      customizeText: yesOrBlank,
    },
  ], [roomLabel]);

  return (
    <DashboardCard icon="bi-door-open" title="Unit/Room/Bed Changes" count={data.length} {...dragProps}>
      {data.length === 0 ? (
        <EmptyState message="No room/bed changes" />
      ) : (
        <CardGrid<DashboardDischargedResident>
          {...rowLinkProps<DashboardDischargedResident>((r) => r.pKey_Patient, RESIDENT_TAB.primaryInfo)}
          ariaLabel="Unit/Room/Bed Changes"
          dataSource={data}
          columns={columns}
          // A resident moved twice in the window has two rows.
          keyExpr={(r) => `${r.pKey_Patient}|${r.dateTimeOfLog}`}
        />
      )}
    </DashboardCard>
  );
}
